import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';

interface Particle {
  x: number;
  y: number;
  r: number;
  v: number;
  a: number;
}

const PARTICLE_COUNT = 60;

/**
 * Fondo de las pantallas sin sesion: resplandor azul sobre grafito y particulas
 * que suben despacio (opcion B del login animado del kit de marca).
 *
 * Va detras del login compartido, que en EVOLTI tiene el fondo transparente
 * (ver `styles.css`). Las particulas se pausan con la pestaña oculta y no se
 * mueven si el sistema pide reducir movimiento.
 */
@Component({
  selector: 'app-auth-background',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="blob b1"></div>
    <div class="blob b2"></div>
    <canvas #canvas aria-hidden="true"></canvas>
  `,
  styles: [
    `
      :host {
        position: fixed;
        inset: 0;
        z-index: 0;
        overflow: hidden;
        pointer-events: none;
        background: var(--ev-ink-900);
      }
      .blob {
        position: absolute;
        border-radius: 50%;
        filter: blur(60px);
      }
      .b1 {
        width: 70vmax;
        height: 70vmax;
        left: -20vmax;
        bottom: -40vmax;
        background: radial-gradient(circle, rgba(0, 0, 255, 0.95), rgba(0, 0, 255, 0) 65%);
        animation: drift1 18s ease-in-out infinite alternate;
      }
      .b2 {
        width: 45vmax;
        height: 45vmax;
        right: -15vmax;
        top: -20vmax;
        background: radial-gradient(circle, rgba(0, 0, 255, 0.5), rgba(0, 0, 255, 0) 65%);
        animation: drift2 22s ease-in-out infinite alternate;
      }
      canvas {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
      }
      @keyframes drift1 {
        to {
          transform: translate(12vmax, -6vmax) scale(1.08);
        }
      }
      @keyframes drift2 {
        to {
          transform: translate(-8vmax, 8vmax) scale(0.92);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .blob {
          animation: none;
        }
      }
    `,
  ],
})
export class AuthBackgroundComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) private canvasRef!: ElementRef<HTMLCanvasElement>;

  private readonly zone = inject(NgZone);
  private ctx: CanvasRenderingContext2D | null = null;
  private particles: Particle[] = [];
  private width = 0;
  private height = 0;
  private frame = 0;
  private running = false;
  private readonly reduceMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  private readonly onResize = () => {
    this.resize();
    if (!this.running) this.draw();
  };
  private readonly onVisibility = () => (document.hidden ? this.stop() : this.start());

  ngAfterViewInit(): void {
    this.ctx = this.canvasRef.nativeElement.getContext('2d');
    if (!this.ctx) return;

    this.resize();
    // El bucle de animacion no toca nada de Angular: fuera de la zona no
    // dispara deteccion de cambios en cada cuadro.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibility);
      if (this.reduceMotion) this.draw();
      else this.start();
    });
  }

  ngOnDestroy(): void {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private start(): void {
    if (this.running || this.reduceMotion || !this.ctx) return;
    this.running = true;
    this.loop();
  }

  private stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
  }

  private loop = (): void => {
    if (!this.running) return;
    for (const p of this.particles) {
      p.y -= p.v;
      if (p.y < -5) {
        p.y = this.height + 5;
        p.x = Math.random() * this.width;
      }
    }
    this.draw();
    this.frame = requestAnimationFrame(this.loop);
  };

  private resize(): void {
    const canvas = this.canvasRef.nativeElement;
    const dpr = window.devicePixelRatio || 1;
    this.width = canvas.width = window.innerWidth * dpr;
    this.height = canvas.height = window.innerHeight * dpr;
    this.particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * this.width,
      y: Math.random() * this.height,
      r: (Math.random() * 1.6 + 0.4) * dpr,
      v: (Math.random() * 0.25 + 0.05) * dpr,
      a: Math.random() * 0.6 + 0.2,
    }));
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.clearRect(0, 0, this.width, this.height);
    for (const p of this.particles) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(170, 170, 255, ${p.a})`;
      ctx.fill();
    }
  }
}
